"use client";
import React, { useState, useEffect, useContext, createContext } from "react";

// Create context
const VisualContext = createContext();

// Provider component
export function VisualProvider({ children }) {
  const [settings, setSettings] = useState({
    colorScheme: 'nebula',
    particleDensity: 'medium',
    animationSpeed: 1,
    effectIntensity: 0.7,
    enableGlow: true,
    enableParticles: true,
    enableAurora: true,
    particleInteraction: true
  });

  // Set color scheme
  const setColorScheme = (scheme) => {
    if (['nebula', 'cosmic', 'aurora'].includes(scheme)) {
      setSettings(prev => ({ ...prev, colorScheme: scheme }));
      
      // Update CSS variables
      document.documentElement.style.setProperty('--color-scheme', scheme);
    }
  };

  // Set effect intensity
  const setEffectIntensity = (intensity) => {
    if (intensity >= 0 && intensity <= 1) {
      setSettings(prev => ({ ...prev, effectIntensity: intensity }));
      
      // Update CSS variables
      document.documentElement.style.setProperty('--effect-intensity', intensity);
    }
  };

  // Set particle density
  const setParticleDensity = (density) => {
    if (['low', 'medium', 'high'].includes(density)) {
      setSettings(prev => ({ ...prev, particleDensity: density }));
      
      // Update CSS variables
      const densityValue = 
        density === 'low' ? '50' : 
        density === 'medium' ? '100' : 
        density === 'high' ? '200' : '100';
      
      document.documentElement.style.setProperty('--particle-density', densityValue);
    }
  };

  // Set animation speed
  const setAnimationSpeed = (speed) => {
    if (speed >= 0.1 && speed <= 2) {
      setSettings(prev => ({ ...prev, animationSpeed: speed }));
      
      // Update CSS variables
      document.documentElement.style.setProperty('--animation-speed', speed);
    }
  };

  // Toggle effects
  const toggleEffect = (effect, enabled) => {
    if (['enableGlow', 'enableParticles', 'enableAurora', 'particleInteraction'].includes(effect)) {
      setSettings(prev => ({ ...prev, [effect]: enabled }));
      
      // Update body classes
      if (effect === 'enableGlow') {
        if (enabled) {
          document.body.classList.add('enable-glow');
        } else {
          document.body.classList.remove('enable-glow');
        }
      } else if (effect === 'enableParticles') {
        if (enabled) {
          document.body.classList.add('enable-particles');
        } else {
          document.body.classList.remove('enable-particles');
        }
      } else if (effect === 'enableAurora') {
        if (enabled) {
          document.body.classList.add('enable-aurora');
        } else {
          document.body.classList.remove('enable-aurora');
        }
      }
    }
  };

  // Apply theme settings when component mounts
  useEffect(() => {
    // Set initial CSS variables
    document.documentElement.style.setProperty('--color-scheme', settings.colorScheme);
    document.documentElement.style.setProperty('--effect-intensity', settings.effectIntensity);
    
    const densityValue = 
      settings.particleDensity === 'low' ? '50' : 
      settings.particleDensity === 'medium' ? '100' : 
      settings.particleDensity === 'high' ? '200' : '100';
    
    document.documentElement.style.setProperty('--particle-density', densityValue);
    document.documentElement.style.setProperty('--animation-speed', settings.animationSpeed);
    
    // Set initial body classes
    if (settings.enableGlow) {
      document.body.classList.add('enable-glow');
    }
    
    if (settings.enableParticles) {
      document.body.classList.add('enable-particles');
    }
    
    if (settings.enableAurora) {
      document.body.classList.add('enable-aurora');
    }
    
    // Cleanup function
    return () => {
      document.body.classList.remove('enable-glow', 'enable-particles', 'enable-aurora');
    };
  }, []);

  // Context value
  const value = {
    settings,
    setColorScheme,
    setEffectIntensity,
    setParticleDensity,
    setAnimationSpeed,
    toggleEffect
  };

  return (
    <VisualContext.Provider value={value}>
      {children}
    </VisualContext.Provider>
  );
}

// Custom hook to use the visual context
export function useVisual() {
  const context = useContext(VisualContext);
  if (context === undefined) {
    throw new Error('useVisual must be used within a VisualProvider');
  }
  return context;
}
